import React, { useRef, useEffect, useState } from 'react';
import { motion, useSpring } from 'framer-motion';

// Interactive 3D Wireframe Constellation Canvas
const ProcessVisual3D = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    // Generate 3D vertices for a geodesic wireframe sphere
    const vertices = [];
    const radius = 100;
    const latDivs = 8;
    const lonDivs = 12;

    for (let i = 0; i <= latDivs; i++) {
      const lat = (i * Math.PI) / latDivs;
      const sinLat = Math.sin(lat);
      const cosLat = Math.cos(lat);

      for (let j = 0; j < lonDivs; j++) {
        const lon = (j * 2 * Math.PI) / lonDivs;
        const sinLon = Math.sin(lon);
        const cosLon = Math.cos(lon);

        vertices.push({
          x: radius * sinLat * cosLon,
          y: radius * sinLat * sinLon,
          z: radius * cosLat,
          latIndex: i,
          lonIndex: j
        });
      }
    }

    let angleX = 0;
    let angleY = 0;
    let targetSpeedX = 0.005;
    let targetSpeedY = 0.005;

    // Connect mouse movement to rotate speed
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      targetSpeedY = (x / rect.width) * 0.04;
      targetSpeedX = (-y / rect.height) * 0.04;
    };

    const handleMouseLeave = () => {
      targetSpeedX = 0.005;
      targetSpeedY = 0.005;
    };

    const resize = () => {
      if (canvas) {
        w = canvas.width = canvas.offsetWidth;
        h = canvas.height = canvas.offsetHeight;
      }
    };

    window.addEventListener('resize', resize);
    canvas.parentElement.addEventListener('mousemove', handleMouseMove);
    canvas.parentElement.addEventListener('mouseleave', handleMouseLeave);

    let frameId;
    let speedX = 0.005;
    let speedY = 0.005;

    const draw = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, w, h);

      // Damp speed rotation updates
      speedX += (targetSpeedX - speedX) * 0.1;
      speedY += (targetSpeedY - speedY) * 0.1;

      angleX += speedX;
      angleY += speedY;

      const cosX = Math.cos(angleX);
      const sinX = Math.sin(angleX);
      const cosY = Math.cos(angleY);
      const sinY = Math.sin(angleY);

      const centerX = w / 2;
      const centerY = h / 2;
      const perspective = 300;

      // Project vertices to 2D
      const projected = vertices.map(v => {
        // Rotate Y
        let x1 = v.x * cosY - v.z * sinY;
        let z1 = v.x * sinY + v.z * cosY;

        // Rotate X
        let y2 = v.y * cosX - z1 * sinX;
        let z2 = v.y * sinX + z1 * cosX;

        // Perspective scaling
        const scale = perspective / (perspective + z2);
        return {
          sx: centerX + x1 * scale,
          sy: centerY + y2 * scale,
          sz: z2,
          latIndex: v.latIndex,
          lonIndex: v.lonIndex
        };
      });

      // Draw lines between vertices
      ctx.lineWidth = 1;
      
      for (let i = 0; i < projected.length; i++) {
        const p1 = projected[i];

        // Fade lines based on depth (z coordinate)
        const alpha = Math.max(0.08, Math.min(0.65, 0.4 - p1.sz / radius));
        ctx.strokeStyle = `rgba(20, 184, 166, ${alpha})`;

        // Connect to next vertex in longitude (horizontal ring)
        const nextLonIdx = p1.latIndex * lonDivs + ((p1.lonIndex + 1) % lonDivs);
        const pNextLon = projected[nextLonIdx];
        if (pNextLon) {
          ctx.beginPath();
          ctx.moveTo(p1.sx, p1.sy);
          ctx.lineTo(pNextLon.sx, pNextLon.sy);
          ctx.stroke();
        }

        // Connect to corresponding vertex in next latitude (vertical link)
        const nextLatIdx = (p1.latIndex + 1) * lonDivs + p1.lonIndex;
        if (nextLatIdx < projected.length) {
          const pNextLat = projected[nextLatIdx];
          ctx.beginPath();
          ctx.moveTo(p1.sx, p1.sy);
          ctx.lineTo(pNextLat.sx, pNextLat.sy);
          ctx.stroke();
        }

        // Draw node points
        const nodeAlpha = Math.max(0.1, Math.min(0.8, 0.5 - p1.sz / radius));
        ctx.fillStyle = `rgba(99, 102, 241, ${nodeAlpha})`;
        ctx.beginPath();
        ctx.arc(p1.sx, p1.sy, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      frameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      if (canvas && canvas.parentElement) {
        canvas.parentElement.removeEventListener('mousemove', handleMouseMove);
        canvas.parentElement.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '380px',
        display: 'block',
      }}
    />
  );
};

// 3D Tilting Step Card Component
const ProcessStepCard = ({ num, title, desc, delay, visible }) => {
  const cardRef = useRef(null);
  const rotateX = useSpring(0, { stiffness: 300, damping: 24 });
  const rotateY = useSpring(0, { stiffness: 300, damping: 24 });

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    // Map to tilt degree (max 8 deg)
    rotateX.set(-y / (rect.height / 2) * 8);
    rotateY.set(x / (rect.width / 2) * 8);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 30 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        perspective: '1000px',
        width: '100%',
      }}
    >
      <div style={{
        background: 'rgba(255, 255, 255, 0.012)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)',
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.4s',
      }}
      className="process-step-inner"
      onMouseOver={(e) => e.currentTarget.style.borderColor = 'rgba(20, 184, 166, 0.2)'}
      onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)'}>
        {/* Step floating number */}
        <div style={{
          fontSize: '15px',
          fontWeight: 800,
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#14b8a6',
          flexShrink: 0,
          transform: 'translateZ(20px)',
        }}>
          {num}
        </div>
        <div style={{ flex: 1, transform: 'translateZ(10px)' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--ink)',
            marginBottom: '8px',
          }}>
            {title}
          </h3>
          <p style={{
            fontSize: '14px',
            fontWeight: 300,
            color: 'var(--ink-2)',
            lineHeight: 1.6,
          }}>
            {desc}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const steps = [
  {
    num: '01',
    title: 'Architecture',
    desc: 'We map the technical landscape, identify bottlenecks, and design the optimal system architecture for your vision.',
  },
  {
    num: '02',
    title: 'Design & Prototype',
    desc: 'Pixel-perfect UI/UX crafted for humans — premium aesthetics fused with deep product thinking.',
  },
  {
    num: '03',
    title: 'Engineering Sprint',
    desc: 'Clean, testable, production-ready code. Every line reviewed, every system load-tested before shipping.',
  },
  {
    num: '04',
    title: 'Scale & Launch',
    desc: 'Cloud-native deployment with auto-scaling infrastructure, continuous monitoring, and zero-downtime releases.',
  },
];

const Process = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section" id="process" ref={ref} style={{ padding: '80px 0' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          style={{ marginBottom: '48px' }}
        >
          <p className="section-label">Our Process</p>
          <h2 className="section-title">
            How we{' '}
            <em style={{
              fontStyle: 'italic',
              color: 'var(--ink-3)',
              fontWeight: 300,
              letterSpacing: '-0.03em',
            }}>
              engineer
            </em>
            <span>.</span>
          </h2>
          <p className="section-subtitle" style={{ maxWidth: '620px' }}>
            A disciplined, battle-tested methodology for transforming high-scale ambitions into robust digital realities.
          </p>
        </motion.div>

        {/* 2-Column Layout: Left Steps column, Right Sticky 3D visual */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: '48px',
          alignItems: 'start',
        }} className="process-layout-grid">
          <style>{`
            @media (max-width: 860px) {
              .process-layout-grid {
                grid-template-columns: 1fr !important;
                gap: 32px !important;
              }
              .sticky-visual-col {
                position: static !important;
                height: 380px !important;
              }
            }
          `}</style>

          {/* Left: Cards column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {steps.map((s, i) => (
              <ProcessStepCard
                key={s.num}
                num={s.num}
                title={s.title}
                desc={s.desc}
                delay={0.1 * i}
                visible={visible}
              />
            ))}
          </div>

          {/* Right: Sticky 3D wireframe constellation panel */}
          <div className="sticky-visual-col" style={{
            position: 'sticky',
            top: '120px',
            background: 'rgba(255, 255, 255, 0.01)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '28px',
            padding: '24px',
            boxShadow: '0 20px 48px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)',
            height: '420px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <ProcessVisual3D />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
